import { Route } from '@angular/router';
import {
  ProjectsFieldDefsResolver,
  ProjectsProjectResolver,
  PipelinesPipelineResolver,
  ProjectDataResolver,
} from './projects.resolvers';
import { ProjectsComponent } from './projects.component';
import { ProjectsSettingsComponent } from './list/settings/settings.component';
import { ProjectsDetailsComponent } from './details/details.component';
import { ProjectsListComponent } from './list/list.component';
import { ProjectsListTableComponent } from './list/table/table.component';
import { ProjectsListGridComponent } from './list/grid/grid.component';
import { ProjectsListKanbanComponent } from './list/kanban/kanban.component';
import { ProjectsListMapComponent } from './list/map/map.component';
import { ProjectPdfComponent } from 'app/modules/projects/project-pdf/project-pdf.component';
import { ProjectMapAddComponent } from 'app/modules/projects/project-map-add/project-map-add.component';
import {
  CanActivateProjectList,
  DealAccessControlGuard,
  ManagePipelineGuard,
  ProjectDeactivateGuard,
  ProjectTabExistsGuard,
  RedirectDetailsTabGuard,
} from './projects.guards';
import { RelatedToType } from 'app/shared/enums';
import { RedirectToNewTaskModuleGuard } from '@tasks/guards/RedirectToNewTaskModule.guard';

export const projectsRoutes: Route[] = [
  {
    path: '',
    component: ProjectsComponent,
    resolve: {
      options: ProjectDataResolver,
      fieldDefs: ProjectsFieldDefsResolver,
    },
    children: [
      {
        path: '',
        component: ProjectsListComponent,
        children: [
          {
            path: 'settings',
            component: ProjectsSettingsComponent,
            data: { canAccess: true },
            canActivate: [ManagePipelineGuard],
          },
          {
            path: ':id/map/add',
            component: ProjectMapAddComponent,
            resolve: {
              pipeline: PipelinesPipelineResolver,
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
            },
          },
          {
            path: ':id/grid',
            component: ProjectsListGridComponent,
            resolve: {
              pipeline: PipelinesPipelineResolver,
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
            },
          },
          {
            path: ':id/kanban',
            component: ProjectsListKanbanComponent,
            resolve: {
              pipeline: PipelinesPipelineResolver,
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
            },
          },
          {
            path: ':id/map',
            component: ProjectsListMapComponent,
            resolve: {
              pipeline: PipelinesPipelineResolver,
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
            },
          },
          {
            path: ':id/list',
            component: ProjectsListTableComponent,
            resolve: {
              pipeline: PipelinesPipelineResolver,
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
            },
            data: {
              relatedToType: RelatedToType.Project,
            },
          },
          {
            path: ':id',
            component: ProjectsListTableComponent,
            canActivate: [CanActivateProjectList],
          },
        ],
      },
      {
        path: ':id/pdf',
        component: ProjectPdfComponent,
        resolve: {
          pipeline: PipelinesPipelineResolver,
        },
      },
      {
        path: ':id/:project_id',
        component: ProjectsDetailsComponent,
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        canActivate: [DealAccessControlGuard, RedirectDetailsTabGuard],
        canDeactivate: [ProjectDeactivateGuard],
        resolve: {
          project: ProjectsProjectResolver,
          options: ProjectDataResolver,
        },
        children: [
          {
            path: 'tasks',
            canActivate: [RedirectToNewTaskModuleGuard],
            data: {
              relatedToType: RelatedToType.Project,
              taskModuleRelatedTo: RelatedToType.Project,
            },
            loadChildren: () =>
              import('../../shared/modules/legacy-tasks/tasks.module').then((m) => m.LegacyTasksTabModule),
          },
          {
            path: 'new-tasks',
            loadChildren: () => import('@tasks/tasks.routes').then((m) => m.routes),
          },
          {
            path: 'notes',
            loadChildren: () => import('../../shared/modules/notes/notes.module').then((m) => m.NotesModule),
          },
          {
            path: 'folders',
            loadChildren: () => import('../../shared/modules/folders/folders.module').then((m) => m.FoldersTabModule),
          },
          {
            path: 'sheets',
            loadChildren: () => import('./details/sheets/sheets.module').then((m) => m.ProjectSheetsModule),
          },
          {
            path: 'insights',
            loadChildren: () => import('./details/insights/insights.module').then((m) => m.ProjectInsightsModule),
          },
          {
            path: 'tenants',
            loadChildren: () => import('./details/tenants/tenants.module').then((m) => m.ProjectTenantsModule),
          },
          {
            path: 'leases',
            loadChildren: () => import('./details/leases/tenants.module').then((m) => m.PropertyTenantsModule),
          },
          {
            path: 'loans',
            loadChildren: () => import('./details/loans/loans.module').then((m) => m.ProjectLoansModule),
          },
          {
            path: 'dispositions',
            loadChildren: () => import('./details/dispositions/dispositions.module').then((m) => m.DispositionsModule),
          },
          {
            path: 'contacts',
            loadChildren: () => import('app/modules/contacts/contacts.module').then((m) => m.ContactsModule),
          },
          {
            path: 'custom_object',
            data: {
              relatedTo: RelatedToType.Project,
            },
            loadChildren: () =>
              import('../../shared/modules/custom-objects/custom-objects.module').then((m) => m.CustomObjectsModule),
          },
        ],
      },
      {
        path: ':id/:project_id/details',
        canDeactivate: [ProjectDeactivateGuard],
        component: ProjectsDetailsComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [DealAccessControlGuard, RedirectDetailsTabGuard],
        resolve: {
          project: ProjectsProjectResolver,
          options: ProjectDataResolver,
          fieldDefs: ProjectsFieldDefsResolver,
          pipeline: PipelinesPipelineResolver,
        },
        children: [
          {
            path: '',
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.ProjectDashboardModule),
          },
          {
            path: 'overview',
            resolve: {
              project: ProjectsProjectResolver,
              pipeline: PipelinesPipelineResolver,
            },
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.ProjectDashboardModule),
          },
          {
            path: 'tabs/:tab_id',
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.ProjectDashboardModule),
            canActivate: [ProjectTabExistsGuard],
          },
        ],
      },
    ],
  },
];
